// SearchAndFilter.tsx
import { format, isSameDay } from "date-fns";
import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { timesheetFilterOptions } from "../../../../models/vacancy";
import { useAppSelector as useSelector } from "../../../../store";
import { TTimesheetSort, TTimesheetVariant } from "../../../../store/timesheet/types";
import CrossUnBold from "../../../UI/Icons/CrossUnBold";
import SortAndFilter from "../../../UI/Icons/SortAndFilter";
import TextInput from "../../../UI/Inputs/TextInput";
import SortFilterDropdown, { SortOrder } from "../../../UI/SortFilterDropdown";
import CandidatesFilter from "./CandidatesFilter";
import CentersFilter from "./CentersFilter";

// 1. Custom Date Input for DatePicker
const CustomDateInput = React.forwardRef<
  HTMLDivElement,
  {
    range: [Date | null, Date | null] | null;
    onClick?: () => void;
    disabled: boolean;
    isSingleDate?: boolean;
    onClear?: () => void;
  }
>(({ range, disabled = false, isSingleDate = false, onClick, onClear }, ref) => {
  
  // Date Format Handler
  const formatDateRange = (
    range: [Date | null, Date | null] | null
  ): string => {
    if (!range) return "";
    const [start, end] = range;
    if (isSingleDate && end) return format(end, "EEE dd MMM yyyy");
    if (!start) return "";
    if (!end || isSameDay(start, end)) return format(start, "EEE dd MMM yyyy");
    return `${format(start, "EEE dd MMM yyyy")} - ${format(
      end,
      "EEE dd MMM yyyy"
    )}`;
  };

  const displayValue = formatDateRange(range);

  return (
    <div
      className="form-control w-100 d-flex align-items-center gap-5"
      onClick={onClick}
      ref={ref}
      style={{ cursor: "pointer" }}
    >
      <div
        className={`d-flex gap-5 align-items-center ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        } ${!displayValue ? "text-black" : ""}`}
      >
        {displayValue || "Showing All Dates"}
        
      
      {displayValue && displayValue !== "" ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClear?.();
          }}
          className="btn-date-clear"
          aria-label="Clear date range"
          tabIndex={0}
        >
          <CrossUnBold />    
        </button>
      ) : (
        <svg
          className={`min-w-12px`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.5 4.0575L9.4425 3L6 6.435L2.5575 3L1.5 4.0575L6 8.5575L10.5 4.0575Z"
            fill="#000000"
          />
        </svg>
      )}
      </div>
     
    </div>
  );
});

interface SearchAndFilterProps {
  variant: TTimesheetVariant;
  keyword: string;
  selectedCentreIds: number[];
  selectedCandidateIds: number[];
  onKeywordChange: (value: string) => void;
  onCentreIdsChange: (ids: number[]) => void;
  onCandidateIdsChange: (ids: number[]) => void;
  dateRange?: [Date | null, Date | null] | null;
  onDateRangeChange?: (range: [Date | null, Date | null] | null) => void;
  handleSortChange?: (field: TTimesheetSort) => void;
}

const SearchAndFilter: React.FC<SearchAndFilterProps> = ({
  variant = "client-pending",
  keyword,
  selectedCentreIds,
  selectedCandidateIds,
  onKeywordChange,
  onCentreIdsChange,
  onCandidateIdsChange,
  dateRange,
  onDateRangeChange,
  handleSortChange
}) => {
  const allCentersList = useSelector((state) => state.timesheet.allCentersList);
  const allCandidatesList = useSelector(
    (state) => state.timesheet.allCandidates
  );
  const [selectedDateRange, setSelectedDateRange] = useState<
    [Date | null, Date | null] | null
  >(dateRange || null);
  const [role, type] = variant.split("-") as [
    "client" | "admin",
    "pending" | "approval" | "history"
  ];
  const [showMobileFilters, setShowMobileFilters] = useState(false);


  const isPending = type === "pending";

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onKeywordChange(e.target.value);
  };

  const handleDateChange = (range: [Date | null, Date | null] | null) => {
    setSelectedDateRange(range);
    if (onDateRangeChange) onDateRangeChange(range);
  };

  const handleClearDateRange = () => {
    setSelectedDateRange(null);
    if (onDateRangeChange) onDateRangeChange(null);
  };

  const searchInput = (
    <TextInput
      placeholder="Search..."
      field="search"
      value={keyword}
      onChange={handleSearchChange}
      showSearchIcon={true}
      floatingLabel={false}
    />
  );

  const renderDateRange = () => {
    return (
      <DatePicker
        selected={selectedDateRange ? selectedDateRange[0] : null}
        onChange={(range) => {
          setSelectedDateRange(range);
        }}
        onCalendarClose={() => {
          if (
            selectedDateRange &&
            selectedDateRange[0] &&
            !selectedDateRange[1]
          ) {
            handleDateChange([selectedDateRange[0], selectedDateRange[0]]);
          } else {
            handleDateChange(selectedDateRange ?? null);
          }
        }}
        startDate={selectedDateRange?.[0]}
        endDate={selectedDateRange?.[1]}
        selectsRange
        customInput={
          <CustomDateInput
            range={selectedDateRange ?? null}
            disabled={false}
            onClear={handleClearDateRange}
          />
        }
        dateFormat="dd MMM yyyy"
        maxDate={new Date()}
        placeholderText="Showing All Dates"
      />
    );
  };

  const handleCentreSelect = (centreIds: number[]) => {
    onCentreIdsChange(centreIds);
  };

  const handleCandidateSelect = (candidateIds: number[]) => {
    onCandidateIdsChange(candidateIds);
  };

  const handleLocalSortChange = (field: string, order: SortOrder) => {
    timesheetFilterOptions.filter(option => {
      if (option.label === field) {
        handleSortChange && handleSortChange(option.value as TTimesheetSort);
        return true;
      }
      return false;
    });
  };

  const handleFilterChange = () => {
    setShowMobileFilters((prev) => !prev);
  };

  return (
    <div className="d-flex flex-column flex-lg-row gap-10 align-items-lg-center justify-content-lg-between position-relative">
      {isPending || variant === "client-approval" ? (
        <div className="form-group w-200px mobile-w-100 input-icon icon-left">
          {searchInput}
        </div>
      ) : (
        <div className="d-flex flex-column flex-lg-row gap-20 align-items-lg-center">
          <div className="form-group w-200px mobile-w-100 input-icon icon-left">
            {searchInput}
          </div>
          <div className="d-flex gap-3 align-items-center">
            <div className="fs-13 fw-400 text-black">Date Range:</div>
            <div className="text-only">{renderDateRange()}</div>
          </div>
        </div>
      )}

      {/* Mobile Sort and Filter Toggle */}
      <div className="d-flex d-lg-none flex-column">
        <div className="h-10px"></div>
        <div
          className="d-flex gap-5 align-items-center justify-content-center"
          style={{ cursor: "pointer" }}
          onClick={handleFilterChange}
        >
          <SortAndFilter />
          <div className="fs-13 fw-700 text-black">Sort and Filter</div>
        </div>
        <div className="h-10px"></div>
        <div className="bg-off-white min-h-1px"></div>
      </div>

       {/* Filters: Always visible on desktop, toggled on mobile */}
      <div
        className={`d-flex flex-column flex-lg-row gap-20 gap-mobile-10 align-items-lg-center fs-13 fw-400 ${
          showMobileFilters ? "d-flex" : "d-none"
        } d-lg-flex`}
      >
        {allCentersList.length > 0 && (
          <div className="d-flex gap-3 align-items-center position-relative">
            <div className="fs-13 fw-400 text-black">Centres</div>
            <CentersFilter
              selectedCentreIds={selectedCentreIds}
              setSelectedCentreIds={(ids) => handleCentreSelect(ids)}
            />
          </div>
        )}
        {allCandidatesList.length > 0 && (
          <div className="d-flex gap-3 align-items-center position-relative">
            <div className="fs-13 fw-400 text-black">
              {role === "admin" ? "Employee" : "Candidates"}
            </div>
            <CandidatesFilter
              selectedCandidateIds={selectedCandidateIds}
              setSelectedCandidateIds={(ids) => handleCandidateSelect(ids)}
            />
          </div>
        )}
      </div>
      {/** Mobile Sort */}
      <div className={`d-block d-lg-none ${ showMobileFilters ? "d-flex" : "d-none"}`}>
        <SortFilterDropdown
          sortFields={["Candidate", "Vacancy", "Job Timeframe"]}
          defaultField="Candidate"
          onChange={handleLocalSortChange}
        />
      </div>
    </div>
  );
};

export default SearchAndFilter;
